import TR from 'utils/TR';
import T from 'utils/T';
import Recoil from 'recoil';
import React from 'react';
import * as Luxon from 'luxon'

import ex from 'utils/ex';
import PageContent from './PageContent';
import HBox from 'components/common/HBox';
import LinearCalendar from 'components/common/LinearCalendar';
import useBldsugrExistDate from 'hooks/communication/useBldsugrExistDate';
import * as AppStore from 'stores/AppStore';
import useForceUpdate from 'hooks/common/useForceUpdate';
import { luxonClone } from 'utils/time';
import VBox from 'components/common/VBox';
import ExerciseDialogGlocoseChart from 'components/charts/ExerciseDialogGlocoseChart';
import ExerciseDialogStepsChart from 'components/charts/ExerciseDialogStepsChart';
import usePopupActive from 'hooks/communication/usePopupActive';
import ExerciseDialogExcerciseTable from 'components/dialogs/ExerciseDialogExcerciseTable';
import ExerciseDialogBloodSugarTable from 'components/dialogs/ExerciseDialogBloodSugarTable';
import ExerciseDialogDietTable from 'components/dialogs/ExerciseDialogDietTable';
import ExerciseDialogInsulinTable from 'components/dialogs/ExerciseDialogInsulinTable';
import useDetectEffect from 'hooks/common/useDetectEffect';

const onDisable = () => false;
export default function Analysis(props) {
  const {
    renderAll,
    front,
    ...rest
  } = props || {};

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);

  const {
    requestBldsugrExistDate,
  } = useBldsugrExistDate();
  const [glocose_exists, setGlocoseExists] = Recoil.useRecoilState(AppStore.assets.glocose_exists2);
  // React.useEffect(() => {
  //   setGlocoseExists(() => ({}));
  // }, [setGlocoseExists]);

  const onPeriodChanged = React.useCallback((start, end) => {
    requestBldsugrExistDate({
      search_start_time: start,
      search_end_time: end,
      patient_no,
      search_type: 'M',
    }, (success, data) => {
      if (success) {
        setGlocoseExists(() => ({ ...(data.bldsugr_date_list.reduce((a, c) => ({ ...a, [c]: true }), {})) }));
      } else {
        setGlocoseExists(() => ({}));
      }
    });
  }, [patient_no, requestBldsugrExistDate, setGlocoseExists]);

  React.useEffect(() => {
    onPeriodChanged(period_start, period_end);
  }, [onPeriodChanged, period_end, period_start]);

  const [selection, setSelection] = Recoil.useRecoilState(AppStore.assets.selected_period);
  const select = React.useCallback((date, end) => {
    setSelection(() => date);
  }, [setSelection]);

  const luxon_start = React.useMemo(() => Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd'), [period_start]);
  const luxon_end = React.useMemo(() => Luxon.DateTime.fromFormat(period_end, 'yyyyMMdd'), [period_end]);

  const timestamp = selection ? selection.valueOf() : 0;
  const exists = Object.keys(glocose_exists).length !== 0;
  React.useEffect(() => {
    if (selection !== null && exists && luxon_start <= selection && selection <= luxon_end) return;
    select(luxon_start);
    // onPeriodChanged(period_start, period_end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamp, onPeriodChanged, patient_no, period_end, period_start, select, luxon_start, luxon_end, exists]);


  const onSelect = React.useCallback((date, end) => (e) => {
    select(date, end);
  }, [select]);

  const month_start = React.useMemo(() => Luxon.DateTime.fromFormat(period_start, 'yyyyMMdd').startOf('month'), [period_start]);
  const month_end = React.useMemo(() => luxonClone(luxon_end).endOf('month'), [luxon_end]);

  const { weeks } = month_end.diff(month_start, ['weeks']);

  const onWheel = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);


  const [data, setData] = React.useState({});

  const {
    bldsugr_graph_list = [],
    // bld_sugr_time,
    // bld_sugr_value,
    step_cnt_graph_list = [],
    // active_time,
    // step_cnt,
    daily_step_cnt,
    daily_step_time,
    postprand2h_step_cnt,
    postprand2h_step_time,
    daily_burn_cal,
    postprand2h_burn_cal,
    max_bldsugr_val,
    max_bldsugr_time,
    max_bldsugr_tmzone_cd,
    min_bldsugr_val,
    min_bldsugr_time,
    min_bldsugr_tmzone_cd,
    max_bldsugr_rise_w,
    avg_bldsugr,
    postprand2h_bldsugr_val,
    total_abnormal_cnt,
    total_high_cnt,
    total_low_cnt,
    avg_intaken_cal,
    total_intaken_cal,
    carbo_rate,
    protein_rate,
    fat_rate,
    total_insulin_amt,
    total_bolus_amt,
    total_giju_amt,
    bolus_inject_list = [],
    // bolus_inject_time,
    // bolus_inject_amt,
  } = data;

  const {
    requestPopupActive,
  } = usePopupActive();

  React.useEffect(() => {
    if (!selection) return;
    requestPopupActive({
      patient_no,
      search_time: (selection || luxon_start).toFormat('yyyyMMddHHmmss'),
    }, (success, data) => {
      if (success) {
        setData(() => data);
      } else {
        setData(() => ({}));
      }
    })
  }, [selection, patient_no, requestPopupActive, luxon_start]);

  const [date, weekday] = React.useMemo(() => [(selection || luxon_start).toFormat('yy.MM.dd'), luxonClone(selection || luxon_start).weekday], [luxon_start, selection]);

  return (
    <div
      {...ex(['Page', 'Analysis'], rest)}
      {...{ onWheelCapture: onWheel }}
    >
      <PageContent {...rest}>
        <HBox>
          <LinearCalendar
            key={period_start + period_end}
            {...{
              date_availables: glocose_exists,
              selection_start: null,
              selection_end: null,
              selection,
              onSelect,
              min: luxon_start,
              max: luxon_end,
              length: Math.ceil(weeks + 3 + 3),
              basis: month_end,
              onPeriodChanged: onPeriodChanged,
              onDisable,
            }}
          />
        </HBox>
        <VBox full>
          <div>
            {date}
          </div>
          <ExerciseDialogGlocoseChart {...{ bldsugr_graph_list }} />
          <ExerciseDialogStepsChart {...{ step_cnt_graph_list }} />
          <VBox>
            <HBox {...ex('Caption')}>
              {T('활동')}
            </HBox>
            <ExerciseDialogExcerciseTable {...{ data }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('혈당')}
            </HBox>
            <ExerciseDialogBloodSugarTable  {...{ data }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('식사')}
            </HBox>
            <ExerciseDialogDietTable {...{ data }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('인슐린')}
            </HBox>
            <ExerciseDialogInsulinTable  {...{ data }} />
          </VBox>
        </VBox>
      </PageContent>
    </div>
  );
}